class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    const form = this.querySelector('form');
    if (!form) return;
    form.addEventListener('input', (event) => {
      const params = this.createSearchParamsForEvent(event);
      // Invalidate now, before the debounce interval can admit an older response.
      FacetFiltersForm.clearError();
      FacetFiltersForm.gate.schedule((generation) => {
        FacetFiltersForm.renderPage(params, event, true, generation);
      });
      FacetFiltersForm.setLoading(true);
    });
    form.addEventListener('submit', (event) => {
      event.preventDefault();
      FacetFiltersForm.renderPage(this.createSearchParamsForEvent(event), event);
    });
    this.querySelector('#FacetsWrapperDesktop')?.addEventListener('keyup', onKeyUpEscape);
  }

  static setListeners() {
    FacetFiltersForm.captureControls();
    window.addEventListener('popstate', () => {
      const generation = FacetFiltersForm.gate.invalidate();
      const params = window.location.search.slice(1);
      if (params === FacetFiltersForm.committedParams) {
        FacetFiltersForm.restoreControls();
        FacetFiltersForm.setLoading(false);
        FacetFiltersForm.clearError();
        return;
      }
      FacetFiltersForm.renderPage(params, null, false, generation);
    });
    document.querySelector('[data-results-retry]')?.addEventListener('click', (event) => {
      if (FacetFiltersForm.failedParams !== null) FacetFiltersForm.renderPage(FacetFiltersForm.failedParams, event);
    });
  }

  static captureControls() {
    FacetFiltersForm.committedControls = Array.from(document.querySelectorAll('facet-filters-form [name]')).map(
      (input) => ({ id: input.id, form: input.form.id, name: input.name, value: input.value, checked: input.checked })
    );
  }

  static restoreControls() {
    FacetFiltersForm.committedControls.forEach((saved) => {
      const form = document.getElementById(saved.form);
      const input = saved.id ? document.getElementById(saved.id) : Array.from(form?.elements || []).find(
        (element) => element.name === saved.name && element.type !== 'checkbox'
      );
      if (!input) return;
      input.value = saved.value;
      if (typeof saved.checked === 'boolean') input.checked = saved.checked;
    });
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll('.js-facet-remove').forEach((element) => element.classList.toggle('disabled', disable));
  }

  static setLoading(loading) {
    const container = document.getElementById('ProductGridContainer');
    container?.setAttribute('aria-busy', String(loading));
    container?.querySelector('.collection')?.classList.toggle('loading', loading);
    ['ProductCount', 'ProductCountDesktop'].forEach((id) => document.getElementById(id)?.classList.toggle('loading', loading));
    document.querySelectorAll('.facets-container .loading__spinner, facet-filters-form .loading__spinner').forEach(
      (spinner) => spinner.classList.toggle('hidden', !loading)
    );
    FacetFiltersForm.toggleActiveFacets(loading);
  }

  static clearError() {
    const error = document.querySelector('[data-results-error]');
    if (error) error.hidden = true;
    FacetFiltersForm.failedParams = null;
  }

  static async renderPage(searchParams, event, updateHistory = true, generation = FacetFiltersForm.gate.invalidate()) {
    const requestUrl = new URL(window.location.href);
    requestUrl.search = searchParams;
    requestUrl.hash = '';
    const normalized = window.ArcticResults.normalizeResultsUrl(
      requestUrl.href, document.querySelector('[data-arctic-results]')?.dataset.resultsDefaultSort
    );
    const params = normalized.url.search.slice(1);
    const sectionId = document.querySelector('[data-arctic-results]')?.dataset.sectionId;
    if (!sectionId || !FacetFiltersForm.gate.isCurrent(generation)) return;
    const signal = FacetFiltersForm.gate.begin(generation);
    const url = new URL(normalized.url);
    url.searchParams.set('section_id', sectionId);
    FacetFiltersForm.clearError();
    FacetFiltersForm.setLoading(true);
    const focusId = document.activeElement?.id;
    try {
      let html = FacetFiltersForm.cache.get(url.href);
      if (!html) {
        const response = await fetch(url.href, { signal });
        if (!response.ok) throw new Error(`Results request failed: ${response.status}`);
        html = await response.text();
      }
      if (!FacetFiltersForm.gate.isCurrent(generation)) return;
      const parsed = new DOMParser().parseFromString(html, 'text/html');
      const grid = parsed.getElementById('ProductGridContainer');
      const count = parsed.getElementById('ProductCount');
      if (!grid || !parsed.getElementById('product-grid') || !count ||
          parsed.querySelector('[data-arctic-results]')?.dataset.sectionId !== sectionId) {
        throw new Error('Results response is missing required markup');
      }
      FacetFiltersForm.validateControls(parsed);
      const drawer = document.querySelector('.mobile-facets__wrapper');
      const originalDrawerControls = new Set(drawer?.querySelectorAll('summary, button') || []);
      const activeFilter = event?.target?.closest('.js-filter');
      const sourceFilter = activeFilter && parsed.getElementById(activeFilter.id);
      const renderEvent = sourceFilter ? event : null;
      const controlsBackup = Array.from(document.querySelectorAll('facet-filters-form form, .active-facets-mobile')).map(
        (element) => ({ element, html: element.innerHTML })
      );
      try {
        FacetFiltersForm.renderFilters(html, renderEvent);
      } catch (error) {
        controlsBackup.forEach(({ element, html: original }) => { element.innerHTML = original; });
        FacetFiltersForm.bindNewDrawerControls(drawer, originalDrawerControls);
        throw error;
      }
      FacetFiltersForm.bindNewDrawerControls(drawer, originalDrawerControls);
      // Card modals are reparented to body by Dawn. Remove only this section's
      // old dialogs before inserting its new product cards.
      document.querySelectorAll('body > [data-card-owner-section]').forEach((modal) => {
        if (modal.dataset.cardOwnerSection !== sectionId) return;
        if (modal.hasAttribute('open')) modal.hide?.();
        modal.remove();
      });
      document.getElementById('ProductGridContainer').innerHTML = grid.innerHTML;
      document.querySelectorAll('#ProductGridContainer .scroll-trigger').forEach(
        (element) => element.classList.add('scroll-trigger--cancel')
      );
      ['ProductCount', 'ProductCountDesktop'].forEach((id) => {
        const target = document.getElementById(id);
        if (target) target.innerHTML = count.innerHTML;
      });
      if (updateHistory && params !== window.location.search.slice(1)) {
        history.pushState({ ...history.state, searchParams: params }, '', normalized.url.pathname + normalized.url.search);
      }
      FacetFiltersForm.committedParams = params;
      FacetFiltersForm.captureControls();
      FacetFiltersForm.cache.set(url.href, html);
      if (FacetFiltersForm.cache.size > 12) FacetFiltersForm.cache.delete(FacetFiltersForm.cache.keys().next().value);
      window.dispatchEvent(new CustomEvent('northbound:surfaces-changed', { detail: { source: 'facets' } }));
      if (event) {
        const focusTarget = (focusId && document.getElementById(focusId)) ||
          document.querySelector('.facets__heading') || document.getElementById('ProductCount');
        if (drawer?.querySelector('.mobile-facets__disclosure[open]') && typeof trapFocus === 'function') {
          const openPanel = drawer.querySelector('.mobile-facets__details[open] .mobile-facets__submenu') || drawer;
          trapFocus(openPanel, focusTarget && openPanel.contains(focusTarget) ? focusTarget : undefined);
        } else if (focusTarget) {
          if (!focusTarget.hasAttribute('tabindex') && focusTarget.tagName === 'SPAN') focusTarget.tabIndex = -1;
          focusTarget.focus({ preventScroll: true });
        }
      }
    } catch (error) {
      if (!FacetFiltersForm.gate.isCurrent(generation) || error.name === 'AbortError') return;
      if (!updateHistory) {
        window.location.replace(normalized.url.href);
        return;
      }
      FacetFiltersForm.restoreControls();
      FacetFiltersForm.failedParams = params;
      const errorPanel = document.querySelector('[data-results-error]');
      if (errorPanel) errorPanel.hidden = false;
    } finally {
      if (FacetFiltersForm.gate.isCurrent(generation)) FacetFiltersForm.setLoading(false);
    }
  }

  static validateControls(parsed) {
    const requiredIds = [
      'FacetFiltersForm', 'FacetFiltersFormMobile', 'FacetSortForm', 'FacetSortDrawerForm',
      'FacetsWrapperDesktop', 'FacetsWrapperMobile', 'ProductCountDesktop',
    ];
    requiredIds.forEach((id) => {
      if (document.getElementById(id) && !parsed.getElementById(id)) {
        throw new Error(`Results response is missing ${id}`);
      }
    });
    ['.active-facets-mobile', '.active-facets-desktop', '.mobile-facets__open', '.mobile-facets__count', '.sorting'].forEach(
      (selector) => {
        if (document.querySelectorAll(selector).length !== parsed.querySelectorAll(selector).length) {
          throw new Error(`Results response is missing ${selector}`);
        }
      }
    );
    parsed.querySelectorAll('select[name="sort_by"]').forEach((select) => {
      if (!select.options.length) throw new Error('Results response has no sort options');
    });
    parsed.querySelectorAll('facet-remove').forEach((remove) => {
      if (!remove.querySelector('a[href]')) throw new Error('Results response has an invalid removal control');
    });
  }

  static bindNewDrawerControls(drawer, previous) {
    if (!drawer) return;
    // Do not call bindEvents on unchanged nodes: Dawn adds duplicate listeners.
    drawer.querySelectorAll('summary').forEach((summary) => {
      if (!previous.has(summary)) summary.addEventListener('click', drawer.onSummaryClick.bind(drawer));
    });
    drawer.querySelectorAll('button').forEach((button) => {
      if (!previous.has(button)) button.addEventListener('click', drawer.onCloseButtonClick.bind(drawer));
    });
  }

  static renderFilters(html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
    const facetDetailsElementsFromFetch = parsedHTML.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter'
    );
    const facetDetailsElementsFromDom = document.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter'
    );

    // Remove facets that are no longer returned from the server
    Array.from(facetDetailsElementsFromDom).forEach((currentElement) => {
      if (!Array.from(facetDetailsElementsFromFetch).some(({ id }) => currentElement.id === id)) {
        currentElement.remove();
      }
    });

    const matchesId = (element) => {
      const jsFilter = event ? event.target.closest('.js-filter') : undefined;
      return jsFilter ? element.id === jsFilter.id : false;
    };

    const facetsToRender = Array.from(facetDetailsElementsFromFetch).filter((element) => !matchesId(element));
    const countsToRender = Array.from(facetDetailsElementsFromFetch).find(matchesId);

    facetsToRender.forEach((elementToRender, index) => {
      const currentElement = document.getElementById(elementToRender.id);
      // Element already rendered in the DOM so just update the innerHTML
      if (currentElement) {
        document.getElementById(elementToRender.id).innerHTML = elementToRender.innerHTML;
      } else {
        if (index > 0) {
          const { className: previousElementClassName, id: previousElementId } = facetsToRender[index - 1];
          // Same facet type (eg horizontal/vertical or drawer/mobile)
          if (elementToRender.className === previousElementClassName) {
            document.getElementById(previousElementId).after(elementToRender);
            return;
          }
        }

        if (elementToRender.parentElement) {
          const parent = document.getElementById(elementToRender.parentElement.id);
          const firstFilter = parent?.querySelector('.js-filter');
          if (firstFilter) firstFilter.before(elementToRender);
          else parent?.appendChild(elementToRender);
        }
      }
    });

    FacetFiltersForm.renderActiveFacets(parsedHTML);
    FacetFiltersForm.renderAdditionalElements(parsedHTML);

    if (countsToRender) {
      const closestJSFilterID = event.target.closest('.js-filter').id;

      if (closestJSFilterID) {
        FacetFiltersForm.renderCounts(countsToRender, event.target.closest('.js-filter'));
        FacetFiltersForm.renderMobileCounts(countsToRender, document.getElementById(closestJSFilterID));

        const newFacetDetailsElement = document.getElementById(closestJSFilterID);
        const newElementSelector = newFacetDetailsElement.classList.contains('mobile-facets__details')
          ? `.mobile-facets__close-button`
          : `.facets__summary`;
        const newElementToActivate = newFacetDetailsElement.querySelector(newElementSelector);

        const isTextInput = event.target.getAttribute('type') === 'text';

        if (newElementToActivate && !isTextInput) newElementToActivate.focus();
      }
    }
  }

  static renderActiveFacets(html) {
    const activeFacetElementSelectors = ['.active-facets-mobile', '.active-facets-desktop'];

    activeFacetElementSelectors.forEach((selector) => {
      const activeFacetsElement = html.querySelector(selector);
      if (!activeFacetsElement) return;
      const target = document.querySelector(selector);
      if (target) target.innerHTML = activeFacetsElement.innerHTML;
    });

    FacetFiltersForm.toggleActiveFacets(false);
  }

  static renderAdditionalElements(html) {
    const mobileElementSelectors = ['.mobile-facets__open', '.mobile-facets__count', '.sorting'];

    mobileElementSelectors.forEach((selector) => {
      const sources = html.querySelectorAll(selector);
      document.querySelectorAll(selector).forEach((target, index) => {
        if (sources[index]) target.innerHTML = sources[index].innerHTML;
      });
    });


  }

  static renderCounts(source, target) {
    const targetSummary = target.querySelector('.facets__summary');
    const sourceSummary = source.querySelector('.facets__summary');

    if (sourceSummary && targetSummary) {
      targetSummary.outerHTML = sourceSummary.outerHTML;
    }

    const targetHeaderElement = target.querySelector('.facets__header');
    const sourceHeaderElement = source.querySelector('.facets__header');

    if (sourceHeaderElement && targetHeaderElement) {
      targetHeaderElement.outerHTML = sourceHeaderElement.outerHTML;
    }

    const targetWrapElement = target.querySelector('.facets-wrap');
    const sourceWrapElement = source.querySelector('.facets-wrap');

    if (sourceWrapElement && targetWrapElement) {
      const isShowingMore = Boolean(target.querySelector('show-more-button .label-show-more.hidden'));
      if (isShowingMore) {
        sourceWrapElement
          .querySelectorAll('.facets__item.hidden')
          .forEach((hiddenItem) => hiddenItem.classList.replace('hidden', 'show-more-item'));
      }

      targetWrapElement.outerHTML = sourceWrapElement.outerHTML;
    }
  }

  static renderMobileCounts(source, target) {
    const targetFacetsList = target.querySelector('.mobile-facets__list');
    const sourceFacetsList = source.querySelector('.mobile-facets__list');

    if (sourceFacetsList && targetFacetsList) {
      targetFacetsList.outerHTML = sourceFacetsList.outerHTML;
    }
  }

  createSearchParamsForEvent(event) {
    const targetForm = event.target.closest('form');
    const mobile = targetForm?.id === 'FacetFiltersFormMobile';
    const params = new URLSearchParams();
    document.querySelectorAll('facet-filters-form form').forEach((form) => {
      const include = mobile ? form.id === 'FacetFiltersFormMobile' :
        ['FacetSortForm', 'FacetFiltersForm', 'FacetSortDrawerForm'].includes(form.id);
      if (include) for (const [name, value] of new FormData(form)) params.append(name, value);
    });
    params.delete('page');
    return params.toString();
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    const url = new URL(event.currentTarget.href);
    url.searchParams.delete('page');
    FacetFiltersForm.renderPage(url.search.slice(1), event);
  }
}

FacetFiltersForm.gate = new window.ArcticResults.ResultsRequestGate();
FacetFiltersForm.cache = new Map();
FacetFiltersForm.committedParams = window.location.search.slice(1);
FacetFiltersForm.committedControls = [];
FacetFiltersForm.failedParams = null;
customElements.define('facet-filters-form', FacetFiltersForm);
FacetFiltersForm.setListeners();

class PriceRange extends HTMLElement {
  constructor() {
    super();
    this.querySelectorAll('input').forEach((element) => {
      element.addEventListener('change', this.onRangeChange.bind(this));
      element.addEventListener('keydown', this.onKeyDown.bind(this));
    });
    this.setMinAndMaxValues();
  }

  onRangeChange(event) {
    this.adjustToValidValues(event.currentTarget);
    this.setMinAndMaxValues();
  }

  onKeyDown(event) {
    if (event.metaKey) return;

    const pattern = /[0-9]|\.|,|'| |Tab|Backspace|Enter|ArrowUp|ArrowDown|ArrowLeft|ArrowRight|Delete|Escape/;
    if (!event.key.match(pattern)) event.preventDefault();
  }

  setMinAndMaxValues() {
    const inputs = this.querySelectorAll('input');
    const minInput = inputs[0];
    const maxInput = inputs[1];
    if (maxInput.value) minInput.setAttribute('data-max', maxInput.value);
    if (minInput.value) maxInput.setAttribute('data-min', minInput.value);
    if (minInput.value === '') maxInput.setAttribute('data-min', 0);
    if (maxInput.value === '') minInput.setAttribute('data-max', maxInput.getAttribute('data-max'));
  }

  adjustToValidValues(input) {
    const value = Number(input.value);
    const min = Number(input.getAttribute('data-min'));
    const max = Number(input.getAttribute('data-max'));

    if (value < min) input.value = min;
    if (value > max) input.value = max;
  }
}

customElements.define('price-range', PriceRange);

class FacetRemove extends HTMLElement {
  constructor() {
    super();
    const facetLink = this.querySelector('a');
    facetLink.setAttribute('role', 'button');
    facetLink.addEventListener('click', this.closeFilter.bind(this));
    facetLink.addEventListener('keyup', (event) => {
      event.preventDefault();
      if (event.code.toUpperCase() === 'SPACE') this.closeFilter(event);
    });
  }

  closeFilter(event) {
    event.preventDefault();
    const form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');
    form.onActiveFilterClick(event);
  }
}

customElements.define('facet-remove', FacetRemove);
